import FilePreview from "@/components/common/FilePreview.tsx";
import { imageFileToBase64 } from "@/utils/userroutes/loginroutes1.ts";
import { FC, useState, useRef } from "react";
import { AiOutlineUpload } from "react-icons/ai";

export interface CustomInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  name?: string;
  value?: string;
  files?: File[] | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  multiple?: boolean;
  label?: string;
  error?: string;
  leftSection?: React.ReactNode;
  accept?: string;
  maxSize?: number;
}
/**
 * A custom File input component with the previwing feature
 * @param name
 * @param value
 * @param onChange
 * @param error
 * @param multiple
 * @param label
 * @param leftSection
 * @param files
 * @param accept
 * @param otherCustomParams
 */
const CustomFileInput: FC<CustomInputProps> = (props) => {
  const {
    name,
    value,
    onChange,
    error,
    multiple,
    label,
    leftSection,
    files = [],
    accept,
    ...rest
  } = props;
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const onFocus = () => {
    setFocused(!focused);
    inputRef.current?.focus();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const handleOnChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const _files = e.target.files;
    setSizeError(null);

    if (_files && _files.length > 0 && props.maxSize) {
      const invalidFiles: File[] = [];
      Array.from(_files).forEach((file) => {
        if (file.size > props.maxSize!) {
          invalidFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        const maxSizeMB = (props.maxSize / (1024 * 1024)).toFixed(0);
        const fileSizeMB = formatFileSize(invalidFiles[0].size);
        setSizeError(
          `File size exceeds maximum allowed size of ${maxSizeMB}MB (Actual: ${fileSizeMB})`
        );
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }
    }

    onChange && onChange(e);
    if (_files && _files.length > 0) {
      const images = Array.from(_files).filter((file) =>
        file.type.match("image.*")
      );
      const base64Images = await Promise.all(
        images.map(
          async (image): Promise<string> =>
            (await imageFileToBase64(image)) ?? ""
        )
      );
      setPreviews(base64Images);
    }
  };

  const focusText = error ? "text-main-red" : "text-dark-blue";
  const focusBg = error ? "bg-main-red/5" : "bg-blue-50/70";
  const focusBorder = error ? "border-main-red" : "border-dark-blue";

  return (
    <div className="relative flex flex-col text-gray-500">
      <label
        // onClick={onFocus}
        onFocus={onFocus}
        tabIndex={0}
        htmlFor={name}
        className={` flex flex-row items-center justify-between p-3 cursor-pointer font-medium ${
          focused ? `py-2 ${focusText}` : "py-3"
        }`}
      >
        {label ?? props.name ?? props.placeholder}
        <AiOutlineUpload className={"w-6 h-6"} />
      </label>
      <button
        type="button"
        className={`flex items-center duration-150 w-full overflow-hidden ${focusBg} ${
          focused ? "h-fit" : "h-0"
        }`}
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        <input
          ref={inputRef}
          autoFocus
          type={"file"}
          multiple={multiple ?? false}
          className={` top-0 left-0 w-full h-full p-2 pt-4 border-none bg-transparent focus:border-none outline-none hidden`}
          onChange={handleOnChange}
          value={value}
          {...(accept && { accept })}
          {...rest}
        />
        {leftSection && <div className="px-2">{leftSection}</div>}
        <section className="flex flex-col gap-2 px-4 py-2">
          <span className="">
            {files?.length} File{files?.length !== 1 ? "s" : ""} Selected. Click
            to choose.
          </span>
          <div className="flex flex-row gap-2 flex-wrap pb-6">
            {/* {previews.map((preview, index) => (
                     <img key={index} src={preview} className="w-full max-w-[10em] aspect-square rounded-md object-cover" />
                  ))} */}
            {files?.map((file, index) => {
              //  const base64Image = await imageFileToBase64(file);
              return (
                <FilePreview
                  key={index}
                  name={file.name}
                  mimetype={file.type}
                  file={file}
                  //  base64={base64Image ?? ''}
                />
              );
            })}
          </div>
        </section>
      </button>
      <hr className={`${focused ? focusBorder : ""} h-[2px] appearance-none`} />
      {error && <span className="text-main-red text-sm">{error}</span>}
      {sizeError && <span className="text-main-red text-sm">{sizeError}</span>}
    </div>
  );
};

export default CustomFileInput;
